/**
 * SnapshotBuffer: the last 32 snapshots ordered by server time, and the
 * lookup the interpolator needs (DESIGN 5.6 "Everyone else"): the pair of
 * snapshots surrounding a render time, or the newest one plus how far past
 * it we are allowed to extrapolate.
 */

import type { SnapMsg } from '@shared/index.js';
import { CLIENT } from '@shared/index.js';

export const SNAPSHOT_RING_SIZE = 32;

export interface SnapSample {
  /** The snapshot at or before renderTime (the oldest one when renderTime precedes everything). */
  a: SnapMsg;
  /** The first snapshot after `a`, or null when renderTime is past the newest. */
  b: SnapMsg | null;
  /** 0..1 position between a.st and b.st; 0 when b is null. */
  alpha: number;
  /**
   * When b is null: ms past a.st to extrapolate with the snapshot velocities,
   * capped at CLIENT.extrapolateMaxMs (beyond that the entities hold). 0 when
   * b exists.
   */
  extrapolateMs: number;
}

export class SnapshotBuffer {
  private readonly ring: (SnapMsg | undefined)[];
  private head = 0; // index of the next write
  private count = 0;

  constructor(readonly capacity: number = SNAPSHOT_RING_SIZE) {
    this.ring = new Array<SnapMsg | undefined>(capacity);
  }

  get length(): number { return this.count; }

  get newest(): SnapMsg | null {
    return this.count === 0 ? null : (this.at(this.count - 1) ?? null);
  }

  get oldest(): SnapMsg | null {
    return this.count === 0 ? null : (this.at(0) ?? null);
  }

  /** i = 0 is the oldest, length - 1 the newest. */
  at(i: number): SnapMsg | undefined {
    if (i < 0 || i >= this.count) return undefined;
    const start = (this.head - this.count + this.capacity) % this.capacity;
    return this.ring[(start + i) % this.capacity];
  }

  /**
   * Insert a snapshot. Snapshots are keyed by `st`: one with the same st as
   * the newest replaces it, an older one is dropped (the WebSocket delivers
   * in order, so this only happens after a reconnect to a different server
   * clock, which `clear()` handles).
   */
  push(snap: SnapMsg): boolean {
    const newest = this.newest;
    if (newest) {
      if (snap.st === newest.st) {
        this.ring[(this.head - 1 + this.capacity) % this.capacity] = snap;
        return true;
      }
      if (snap.st < newest.st) return false;
    }
    this.ring[this.head] = snap;
    this.head = (this.head + 1) % this.capacity;
    if (this.count < this.capacity) this.count++;
    return true;
  }

  clear(): void {
    this.ring.fill(undefined);
    this.head = 0;
    this.count = 0;
  }

  /**
   * Find the snapshots around `renderTime` (server ms). Null when empty.
   * - before the oldest: a = oldest, b = next (alpha 0) -> hold at the oldest;
   * - between two: linear alpha;
   * - after the newest: a = newest, b = null, extrapolateMs capped.
   */
  sample(renderTime: number): SnapSample | null {
    if (this.count === 0) return null;
    const newest = this.at(this.count - 1) as SnapMsg;
    if (renderTime >= newest.st) {
      const past = renderTime - newest.st;
      return { a: newest, b: null, alpha: 0, extrapolateMs: Math.min(past, CLIENT.extrapolateMaxMs) };
    }
    // Walk back from the newest: snapshots arrive 10/s and renderTime trails
    // by ~150 ms, so the answer is almost always within the last three.
    for (let i = this.count - 2; i >= 0; i--) {
      const a = this.at(i) as SnapMsg;
      if (renderTime >= a.st) {
        const b = this.at(i + 1) as SnapMsg;
        const span = b.st - a.st;
        const alpha = span > 0 ? (renderTime - a.st) / span : 1;
        return { a, b, alpha, extrapolateMs: 0 };
      }
    }
    const oldest = this.at(0) as SnapMsg;
    const next = this.count > 1 ? (this.at(1) as SnapMsg) : null;
    return { a: oldest, b: next, alpha: 0, extrapolateMs: 0 };
  }
}
